import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon } from 'lucide-react';
import type { BackRef } from '@/features/smr-admin/types/backRef';
import type { SecurityGroupRecord } from '@/features/security-admin/types/security-admin.types';

interface Props {
  data: SecurityGroupRecord[];
  backRef?: BackRef;
  emptyMessage?: string;
}

export function DealerSecurityGroupDataTable({ data, backRef, emptyMessage }: Props) {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<SecurityGroupRecord>[]>(
    () => {
      const navState = backRef ? { backRef } : undefined;

      return [
        { accessorKey: 'name', header: 'Name' },
        { accessorKey: 'dealers_count', header: 'Dealers' },
        { accessorKey: 'products_count', header: 'Products' },
        { accessorKey: 'users_count', header: 'Users' },
        {
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => (
            <PermissionGuard permission={{ resource: 'security_group', action: 'read' }}>
              <Tooltip content="View security group">
                <TextLink
                  onClick={() => navigate(`/admin/security-groups/${row.original.id}`, { state: navState })}
                  color="primary"
                  ariaLabel="View security group"
                  startIcon={<EyeIcon size={20} />}
                />
              </Tooltip>
            </PermissionGuard>
          ),
        },
      ];
    },
    [navigate, backRef],
  );

  return <Table<SecurityGroupRecord> data={data} columns={columns} enableSorting={false} emptyMessage={emptyMessage} />;
}
